import { useCallback, useEffect, useRef, useState } from "react";
import { adminRefreshBus } from "../utils/adminRefreshBus";

/**
 * useAutoRefresh — single source of truth for admin-dashboard data freshness.
 *
 * Behaviour (iter163):
 *   1. Fetches once on mount.
 *   2. Re-fetches when the browser tab regains focus (window `focus` event)
 *      or when the document becomes visible again (`visibilitychange` → visible).
 *   3. Polls on an interval (default 60s), paused automatically while the tab
 *      is hidden to save battery and API quota.
 *   4. Any component can call `adminRefreshBus.emit(scope)` after a mutation
 *      and every mounted hook subscribed to `scope` will refetch silently.
 *   5. Returns `refresh()` for post-mutation refetch (equivalent to
 *      `adminRefreshBus.emit(scope)` but scoped to the current hook).
 *   6. Returns `lastUpdated` (Date) so UI can show "Updated 12s ago".
 *
 * Usage:
 *   const { data, loading, error, refresh, lastUpdated } = useAutoRefresh(
 *     async () => (await authAxios.get("/api/vendors/admin/list")).data,
 *     { scope: "vendors", interval: 60000 }
 *   );
 *   // In a mutation handler:
 *   await authAxios.put(...); refresh(); // or adminRefreshBus.emit("vendors");
 *
 * Options:
 *   scope         — bus channel this hook listens to (string). Any mutation
 *                   handler elsewhere in the app can trigger this hook via
 *                   `adminRefreshBus.emit(scope)`.
 *   interval      — polling interval in ms (default 60_000). Set 0 to disable.
 *   focusRefresh  — refetch on window focus / visibility (default true).
 *   enabled       — if false, no fetching happens (use for feature-flagged tabs).
 *   silent        — if true, polling/focus refetches won't flip `loading` back
 *                   to true (default true — keeps the UI stable).
 */
export const useAutoRefresh = (
  fetchFn,
  {
    scope = null,
    interval = 60_000,
    focusRefresh = true,
    enabled = true,
    silent = true,
    deps = [],
  } = {}
) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Keep the latest fetchFn in a ref so the window listeners stay stable even
  // when the parent re-renders with a new closure.
  const fetchFnRef = useRef(fetchFn);
  useEffect(() => { fetchFnRef.current = fetchFn; }, [fetchFn]);

  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);

  const refresh = useCallback(async ({ background = false } = {}) => {
    if (!enabled) return;
    if (inFlightRef.current) return;        // dedupe parallel refreshes
    inFlightRef.current = true;
    try {
      if (!background || !silent) setLoading(true);
      const result = await fetchFnRef.current();
      if (!mountedRef.current) return;
      setData(result);
      setError(null);
      setLastUpdated(new Date());
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e);
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) setLoading(false);
    }
  }, [enabled, silent]);

  // Initial fetch + explicit deps
  useEffect(() => {
    mountedRef.current = true;
    if (enabled) refresh({ background: false });
    return () => { mountedRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  // Bus subscription
  useEffect(() => {
    if (!scope || !enabled) return undefined;
    const unsub = adminRefreshBus.on(scope, () => refresh({ background: true }));
    return unsub;
  }, [scope, enabled, refresh]);

  // Focus + visibility refetch
  useEffect(() => {
    if (!focusRefresh || !enabled) return undefined;
    const onFocus = () => refresh({ background: true });
    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh({ background: true });
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [focusRefresh, enabled, refresh]);

  // Polling (paused when hidden)
  useEffect(() => {
    if (!interval || !enabled) return undefined;
    const id = setInterval(() => {
      if (document.visibilityState === "visible") refresh({ background: true });
    }, interval);
    return () => clearInterval(id);
  }, [interval, enabled, refresh]);

  return { data, loading, error, refresh, lastUpdated };
};

export default useAutoRefresh;
